// x-accordions.js

/*
Scripts for both CK5 and 4.
CK5 version is built in the editor. Script here is for non-blink browsers awaiting css support.
CK4 version is build in client side JS when accordion class is used on a heading
*/

export default function xAccordions() {

  /*
  CK5 version

  Calculates details height in px to enable transition when no support for auto height (non-blink browsers).
  Script can be deprecated once Safari and FF have sufficient css support. No support as of August 2025.

  Variables for heights are added and updated, then used in the less-file:
  - When opening/closing details on click.
  - When resize or mutations occur.
  */

  const vrtxDetailsAll = document.querySelectorAll(".vrtx-details");

  if (vrtxDetailsAll.length) {
    vrtxDetailsAll.forEach(details => {
      const summary = details.querySelector("summary");
      const content = details.querySelector("vrtx-details-content");
      let initialWidth = details.offsetWidth;

      // If no content, stop. (Can occur if native details in source).
      if (!content) {
        console.debug("No vrtx-details-content found. Possible native details in source.")
        summary.textContent = "//// Error. Unsupported element.";
        return;
      }

      // Function to update heights based on current dimensions
      const updateHeights = () => {
        const closedHeight = summary.offsetHeight;
        const openHeight = closedHeight + content.scrollHeight;

        details.style.setProperty('--closed', closedHeight + 'px');
        details.style.setProperty('--open', openHeight + 'px');
      };

      // Run inital height update
      updateHeights();

      // On summary click, update heights
      summary.addEventListener("click", () => {
        // Safari is slow to get the heights, so making several attempts using interval
        const checkHeight = setInterval(() => {
          if (content.scrollHeight > 0) {
            updateHeights();
            clearInterval(checkHeight);
          }
        }, 100);
      });

      // Observe mutations
      // Accomodate changes inside details that require height update, typically expansions.
      // Note: There needs to be a change in the dom for this to work, not just a class change.
      // To trigger, change textContent, append or something.
      const mutationObserver = new MutationObserver(() => {
        updateHeights();
      });

      mutationObserver.observe(content, {
        childList: true,
        subtree: true,
        characterData: true
      });

      // Observe resize
      const resizeObserver = new ResizeObserver(() => {
        updateHeights();
      });

      resizeObserver.observe(details);
    });
  }

  /*
  CK4 version

  When encountering a heading (h2, h3 or h4) with class="accordion",
  it wraps this and the content after it in a details container.
  It also wraps the heading in a summary container,
  and the content after the summary in a div.
  */

  const accordions = document.querySelectorAll('.accordion:is(h2, h3, h4)');

  accordions.forEach(accordion => {

    // Create accordion elements
    const details = document.createElement('details');
    const summary = document.createElement('summary');
    const contentWrapper = document.createElement('div');

    // Add classes
    details.classList.add('accordion-wrapper');
    contentWrapper.classList.add('accordion-content');

    // Append
    summary.appendChild(accordion.cloneNode(true));
    details.appendChild(summary);
    details.appendChild(contentWrapper);



    /*******************************************************************************************
    * Dependencies when encountering elements that end the accordion and/or start a new one
    ********************************************************************************************/

    let nextElement = accordion.nextElementSibling;
    while (nextElement) {

      // Ends when encountering CK5 accordion (edge case)
      if (nextElement.classList.contains('vrtx-details')) {
        break;
      }
      // Ends when encountering new heading with accordion-class
      if (nextElement.classList.contains('accordion')) {
        break;
      }
      // Ends when encountering any element with accordion-end class
      if (nextElement.classList.contains('accordion-end')) {
        break;
      }
      // Ends when encountering h2 (applies to all h2, h3, and h4 level accordions)
      if (nextElement.tagName.toLowerCase() === 'h2') {
        break;
      }
      // If accordion is h3-level, ends when encountering h3
      if (accordion.tagName.toLowerCase() === 'h3' && nextElement.tagName.toLowerCase() === 'h3') {
        break;
      }
      // If accordion is h4-level, ends when encountering h3 or h4
      if (accordion.tagName.toLowerCase() === 'h4' && (nextElement.tagName.toLowerCase() === 'h3' || nextElement.tagName.toLowerCase() === 'h4')) {
        break;
      }

      const currentElement = nextElement;
      nextElement = nextElement.nextElementSibling;
      contentWrapper.appendChild(currentElement);
    }
    accordion.parentNode.replaceChild(details, accordion);



    /***************************************************************************
    * Animate accordion height
    * A bit difficult, due to how details/summary handles display/visibility
    ****************************************************************************/

    // Height-check on document load. Briefly open and close details to get height.
    details.open = true;
    setTimeout(() => {
      details.open = false;
    }, 0);

    // Add class to enable animation. Timeout to prevent animating initial height-check close.
    setTimeout(() => {
      details.classList.add('animatable');
    }, 500);

    // Add height-variables for css
    details.style.setProperty('--collapsed', summary.offsetHeight + 'px');
    details.style.setProperty('--expanded', summary.offsetHeight + contentWrapper.offsetHeight + 'px');

    // On horizontal resize, close details and update collapsed height
    const initialDetailsWidth = details.offsetWidth;

    window.addEventListener('resize', function() {
      const currentDetailsWidth = details.offsetWidth;

      // If details width is changed..
      if (initialDetailsWidth !== currentDetailsWidth) {

        // Close accordion
        details.open = false;

        // Update collapsed height in case of summary heading text wrap or something.
        setTimeout(() => {
          details.style.setProperty('--collapsed', summary.offsetHeight + 'px');
        }, 500);
      }
    });

    // On details click, update expanded height (in case changed due to resize or something).
    details.addEventListener('click', function() {
      if (!details.open) {
        setTimeout(() => {
          details.style.setProperty('--expanded', summary.offsetHeight + contentWrapper.offsetHeight + 'px');
        }, 300); // Timed with transition duration
      }
    });



    /*****************************************************************************************************
    * Observe content changes inside an accordion and update expanded height if needed.
    * NOTE: There needs to be a change in the dom for this to work. It won't trigger on a class change.
    * To trigger, change textContent, append or something.
    *****************************************************************************************************/

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(() => {
        if (details.open) {
          details.style.setProperty('--expanded', summary.offsetHeight + contentWrapper.offsetHeight + 'px');
        }
      });
    });

    // Start observing the contentWrapper for changes in its child elements
    observer.observe(contentWrapper, { childList: true, subtree: true });
  });
}